import { findIntegrationByName, getIntegrations, updateIntegration, type IntegrationWithJobInfo } from "@/api/integration"
import { Input } from "@/components/form/Input.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useAsyncEffect } from "@/utils"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { ConnectLogo } from "../connect-logo"
import { PortalLoginSection } from "../portal_login_section"
import { use_connect_redirect_to } from "../use_connect_redirect_to"

/** `integration.name` of the row that holds the brand's Satori login (`SpinsConfig`). */
export const SPINS_INTEGRATION = "spins"
export const SATORI_URL = "https://satori.spins.com"

/** `sanzo,  Sanzo Sparkling` → `["SANZO", "SANZO SPARKLING"]`: brand names as SPINS prints them, once each. */
export function spins_brands_from_input(value: string): string[] {
  const names = value
    .split(/[,\n]/)
    .map((name: string) => name.split(/\s+/).filter(Boolean).join(" ").toUpperCase())
    .filter(Boolean)
  return [...new Set(names)]
}

/** The brand names a row already holds, as the field shows them. */
export function spins_brands_to_input(data: unknown): string {
  const brands = data && typeof data === "object" ? (data as Record<string, unknown>).brands : null
  return Array.isArray(brands) ? brands.filter((brand: unknown) => typeof brand === "string").join(", ") : ""
}

interface SpinsLoginProps {
  row: IntegrationWithJobInfo | undefined
  onSaved: () => void
}

/**
 * The Satori login and the brand names on one form. The login is saved first (that creates the
 * row and schedules the daily sync); the brand names are saved onto it next, which is what starts
 * the first run, so that run already knows which brand to build the extracts for.
 */
function SpinsLogin({ row, onSaved }: SpinsLoginProps) {
  const brands_form = useFormState({ brands: spins_brands_to_input(row?.data) })

  async function save_brands(integration: { id: string }): Promise<string | null> {
    const res = await updateIntegration(integration.id, { data: { brands: spins_brands_from_input(brands_form.brands.value) } })
    return res.ok ? null : res.error.message || "The login was saved, but the brand names were not"
  }

  return (
    <PortalLoginSection
      integration_name={SPINS_INTEGRATION}
      portal_label="Satori"
      username_label="Satori email"
      username={row?.username}
      onSaved={onSaved}
      after_save={save_brands}
      extra_fields={
        <Input
          label="Your brand, as SPINS spells it (optional)"
          type="text"
          name="spins_brands"
          required={false}
          placeholder="Leave blank to use the brand name in your distributor sales data"
          bind={brands_form.brands}
          noAutofill
        />
      }>
      <p class="text-sm text-gray-500">
        We pull your SPINS data from{" "}
        <a href={SATORI_URL} target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">
          Satori
        </a>{" "}
        after every SPINS release: your items week by week at each retailer, for forecasts and promotion lift, and your
        category over 4, 12, 24 and 52 weeks, for sales analysis.
      </p>
      <p class="text-xs text-gray-400">
        We set up two extracts in your Satori account, “MarginWiz Weekly” and “MarginWiz Category”, and refresh them
        ourselves. Nothing else in your account is changed. Separate brand names with commas.
      </p>
      <p class="text-xs text-gray-400">
        Leave the brand blank and we use the one your distributor sales data prints, which is how SPINS spells it. If
        you enter one, use the brand on the package, not your company name. If you change it later, we rebuild
        “MarginWiz Weekly” for the new name on the next sync.
      </p>
    </PortalLoginSection>
  )
}

/** Connect (or change) the brand's SPINS Satori login. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectSpinsPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const [row, setRow] = useState<IntegrationWithJobInfo | undefined>(undefined)
  const [saved, setSaved] = useState(false)
  const [reloads, setReloads] = useState(0)

  useAsyncEffect(async () => {
    const res = await getIntegrations({ name: SPINS_INTEGRATION })
    if (res.ok) {
      setRow(findIntegrationByName(res.value.data, SPINS_INTEGRATION))
    }
  }, [reloads])

  function on_saved() {
    setSaved(true)
    setReloads((n: number) => n + 1)
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6">
        <ConnectLogo src="/img/integration/spins.png" alt="SPINS" />
        <h3 class="text-base font-semibold text-gray-900 text-center">SPINS</h3>
        <SpinsLogin key={row?.id ?? "none"} row={row} onSaved={on_saved} />
        <Button class="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800" onClick={() => setLocation(redirectTo)}>
          {saved ? "Done" : "Back"}
        </Button>
      </div>
    </CenterColumnContainer>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("SPINS brand names", () => {
    it("are kept as SPINS prints them, once each", () => {
      expect(spins_brands_from_input("sanzo,  Sanzo Sparkling\nSANZO, ")).toEqual(["SANZO", "SANZO SPARKLING"])
      expect(spins_brands_from_input("")).toEqual([])
    })

    it("show what the row already holds", () => {
      expect(spins_brands_to_input({ brands: ["SANZO", "SANZO SPARKLING"], category_extract_id: 531208 })).toBe("SANZO, SANZO SPARKLING")
      expect(spins_brands_to_input({})).toBe("")
      expect(spins_brands_to_input(null)).toBe("")
    })
  })
}
